import * as React from 'react'
import styled, { css } from 'styled-components'
import theme from 'theme'
import TrashIcon from 'assets/trash.svg'
import priceString from 'utils/priceString'
import useTranslations from 'hooks/useTranslations'

type Props = {
  promotionData: {
    reduction: number
    code: string
    type: string
    value: number
  }
  onRemove: () => void
  noBorder?: boolean
  'data-cy-state': string
}

export function CartPromotionItem(props: Props) {
  const t = useTranslations<'molecules-account-CartPromotionItem'>()

  const snippetForPercentOrAbsolute = () => {
    switch (props.promotionData.type) {
      case 'absolute':
        return t('cartpromoitem_voucher_currency', {
          value: props.promotionData.value * -1
        })
      case 'percentage':
        return t('cartpromoitem_voucher_percentage', {
          value: props.promotionData.value * -1
        })
      default:
        return t('cartpromoitem_voucher')
    }
  }
  return (
    <Wrapper
      className="CartItem"
      $noBorder={props.noBorder}
      data-cy-ctx="molecules/account/CartPromotionItem"
      data-cy-state={props['data-cy-state']}
    >
      <div className="cart-item-image">
        <div className="image-container">
          <img src="https://res.cloudinary.com/lusini/image/upload/c_scale,h_68,w_68/v1664947108/application/icons/promotion.png" />
        </div>
      </div>
      <div className="cart-item-info">
        <div className="cart-item-title">
          <div className="title">{snippetForPercentOrAbsolute()}</div>
        </div>
        <div className="sub-title">
          {t('cartpromoitem_voucher_minimal_value')}
        </div>
        <div className="delivery-sku">
          <div className="sku-info">
            {t('cartpromoitem_sku_label')} {props.promotionData.code}
          </div>
        </div>
        <div className="cart-item-price">
          <div className="prices">
            {'-' + priceString(props.promotionData.reduction * -1)}
          </div>
        </div>
        <div className="actions">
          <div
            onClick={props.onRemove}
            className="remove"
            data-cy-handle="delete-btn"
          >
            <TrashIcon />
            <span>{t('cartpromoitem_delete')}</span>
          </div>
        </div>
      </div>
      <div className="cart-item-price">
        <div className="prices">
          {'-' + priceString(props.promotionData.reduction * -1)}
        </div>
      </div>
    </Wrapper>
  )
}

export default CartPromotionItem

const Wrapper = styled.div<{ $noBorder?: boolean }>`
  display: flex;
  justify-content: space-between;
  padding: ${theme.spacing('m')};
  ${(p) =>
    !p.$noBorder &&
    css`
      border-bottom: 1px solid ${theme.colors.shade.b5};
    `}
  column-gap: ${theme.spacing('m')};
  background: white;

  &.loading {
    justify-content: center;
    align-items: center;
  }
  > .cart-item-image {
    > .image-container {
      max-width: 90px;
      padding: ${theme.spacing('xs')};
      border: 1px solid ${theme.colors.shade.b5};
      background-color: ${theme.colors.white};
    }
  }
  > .cart-item-info {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    row-gap: 5px;

    > .cart-item-title {
      display: flex;
      flex-direction: row;

      > .promotion-additional-info {
        ${theme.ty('rc-s', '700')}
        @media (min-width: ${theme.ms.MD}px) {
          ${theme.ty('rc-l', '700')}
        }
        color: ${theme.colors.b0};
      }
      > .title {
        ${theme.ty('rc-s', '700')}
        @media (min-width: ${theme.ms.MD}px) {
          ${theme.ty('rc-l', '700')}
        }
        color: ${theme.colors.b0};
      }
    }
    > .sub-title {
      ${theme.ty('r-xs')}
      @media (min-width: ${theme.ms.MD}px) {
        ${theme.ty('r-s')}
      }
    }

    color: ${theme.colors.shade.b2};
    > .delivery-sku {
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
      column-gap: 20px;
      > .sku-info {
        ${theme.ty('r-xs')}
        @media (min-width: ${theme.ms.MD}px) {
          ${theme.ty('r-base')}
        }

        color: ${theme.colors.shade.b2};
      }
    }
    > .actions {
      margin-top: ${theme.spacing('s')};
      > .remove {
        cursor: pointer;
        display: flex;
        > svg {
          width: 12px;
        }
        > span {
          margin-left: ${theme.spacing('xs')};
          ${theme.ty('r-xs')}
          @media (min-width: ${theme.ms.MD}px) {
            ${theme.ty('r-s')}
          }
          color: ${theme.colors.shade.b2};
          text-decoration: underline;
        }
        > span:hover {
          text-decoration: none;
        }
      }
    }
    /* prices block visible only mobile */
    > .cart-item-price {
      display: block;
      @media (min-width: ${theme.ms.MD}px) {
        display: none;
      }
      > .prices {
        ${theme.ty('r-s', '700')}
        color: ${theme.colors.accent.green};
        display: flex;
        column-gap: ${theme.spacing('xxs')};
        justify-content: flex-start;
      }
    }
  }
  /* prices block visible only not mobile */
  > .cart-item-price {
    display: none;
    @media (min-width: ${theme.ms.MD}px) {
      display: block;
    }
    > .prices {
      ${theme.ty('r-base', '700')}
      color: ${theme.colors.accent.green};
      display: flex;
      column-gap: ${theme.spacing('xxs')};
      justify-content: flex-end;
    }
  }
`
